import { useState, useEffect, useCallback } from 'react';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, FileText, File, Image, FileSpreadsheet, Download, ChevronLeft, ChevronRight, Loader2, Search } from 'lucide-react';

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function fileIcon(contentType) {
  if (!contentType) return File;
  if (contentType.startsWith('image/')) return Image;
  if (contentType.includes('pdf')) return FileText;
  if (contentType.includes('sheet') || contentType.includes('csv') || contentType.includes('excel')) return FileSpreadsheet;
  return File;
}

const MobileDocuments = () => {
  const { api } = useMobileAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [grouped, setGrouped] = useState({});
  const [allDocs, setAllDocs] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/mobile/documents');
      setGrouped(res.data.grouped || {});
      setAllDocs(res.data.documents || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const folderNames = Object.keys(grouped).sort();
  const totalDocs = allDocs.length;

  // ── Folder detail view ──────────────────────────────────────────
  if (activeFolder) {
    const docs = grouped[activeFolder] || [];
    return (
      <MobileLayout>
        <div className="bg-[var(--m-bg-card)] px-4 py-4 sticky top-0 z-10 safe-top">
          <div className="flex items-center gap-3">
            <button onClick={() => setActiveFolder(null)} className="text-[var(--m-text-body)]" data-testid="folder-detail-back">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <FolderOpen className="h-5 w-5 text-[#ffd108] flex-shrink-0" />
            <h1 className="text-[var(--m-text)] font-bold text-lg flex-1 truncate">{activeFolder}</h1>
            <span className="text-xs text-[var(--m-text-secondary)]">{docs.length} file{docs.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        <div className="p-4 space-y-2" data-testid="folder-detail-page">
          {docs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-[var(--m-text-muted)]">
              <FileText className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">No documents in this folder</p>
            </div>
          ) : (
            docs.map(doc => {
              const Icon = fileIcon(doc.content_type);
              return (
                <a
                  key={doc.id}
                  href={doc.blob_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-[var(--m-bg-card)] rounded-lg px-4 py-3.5 active:bg-[var(--m-bg-elevated)] transition-colors"
                  data-testid={`mobile-doc-${doc.id}`}
                >
                  <Icon className="h-5 w-5 text-[var(--m-text-secondary)] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[var(--m-text)] truncate">{doc.filename}</p>
                    {doc.description && <p className="text-xs text-[var(--m-text-muted)] truncate mt-0.5">{doc.description}</p>}
                    <p className="text-[10px] text-gray-600 mt-0.5">{formatBytes(doc.file_size)}</p>
                  </div>
                  <Download className="h-4 w-4 text-[var(--m-text-muted)] flex-shrink-0" />
                </a>
              );
            })
          )}
        </div>
      </MobileLayout>
    );
  }

  // ── Folders grid view ───────────────────────────────────────────
  return (
    <MobileLayout>
      <div className="bg-[var(--m-bg-card)] px-4 py-4 sticky top-0 z-10 safe-top">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-[var(--m-text-body)]" data-testid="mobile-docs-back">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h1 className="text-[var(--m-text)] font-bold text-lg flex-1">Documents</h1>
          <span className="text-xs text-[var(--m-text-secondary)]">{totalDocs} file{totalDocs !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div className="p-4" data-testid="mobile-documents-page">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--m-text-muted)]" />
          </div>
        ) : folderNames.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[var(--m-text-muted)]">
            <FolderOpen className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">No documents shared with you yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {folderNames.map(folder => {
              const docs = grouped[folder] || [];
              return (
                <button
                  key={folder}
                  type="button"
                  onClick={() => setActiveFolder(folder)}
                  className="bg-[var(--m-bg-card)] border border-[var(--m-border)] rounded-xl p-4 flex flex-col items-center gap-2.5 active:bg-[var(--m-bg-elevated)] transition-colors text-center"
                  data-testid={`folder-card-${folder}`}
                >
                  <FolderOpen className="h-8 w-8 text-[#ffd108]" />
                  <span className="text-sm font-medium text-[var(--m-text)] truncate w-full">{folder}</span>
                  <span className="text-[11px] text-[var(--m-text-muted)]">{docs.length} file{docs.length !== 1 ? 's' : ''}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </MobileLayout>
  );
};

export default MobileDocuments;
